import { Video, Users, Zap, type LucideIcon } from "lucide-react";
import type { OfferingType } from "@/types/offering";

/**
 * Single source of truth for offering-type identity (colour + icon), canonical
 * per the search-card palette (ADR-088, highest-traffic surface): 1-on-1 =
 * emerald/Video, Group = violet/Users, Crash course = amber/Zap. Every surface
 * that renders an offering type — cards, detail pages, the creation wizard,
 * sidebar links, dashboard rails — should read from this map rather than
 * hardcoding its own colours, so the same type never reads as two identities
 * a click apart.
 */
export type OfferingTypeTheme = {
  icon: LucideIcon;
  typeLabel: string;
  /** Two-stop gradient for a thin accent rail (search-card style). */
  rail: string;
  /** Coloured text only, light + dark. */
  accent: string;
  /** Light pill/badge background + text, light + dark. */
  pill: string;
  /** Solid dot indicator background. */
  dot: string;
  /** Solid button background + hover + white text, light + dark. */
  solid: string;
  /** Active-state background + white text for `data-[state=active]` tab triggers, light + dark. */
  tabActive: string;
  /** Border colour, light + dark. */
  border: string;
  /** Soft full-width panel background (banners, rail headers, wizard cards), light + dark. */
  soft: string;
  /** Icon-chip circle background + icon colour, light + dark. */
  iconChip: string;
  /** Two-stop gradient for hero banners on detail pages. */
  gradient: string;
  /** Tinted header-band gradient + its bottom border, for the search-card header (light & dark). */
  headerBg: string;
  /** Title colour on card hover — ties the whole card to its type colour. */
  titleHover: string;
};

export const OFFERING_TYPE_THEME: Record<OfferingType, OfferingTypeTheme> = {
  ONE_TO_ONE: {
    icon: Video,
    typeLabel: "1-on-1",
    rail: "from-emerald-500 to-amber-500",
    accent: "text-emerald-700 dark:text-emerald-300",
    pill: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
    dot: "bg-emerald-500",
    solid: "bg-emerald-600 hover:bg-emerald-700 text-white dark:bg-emerald-500 dark:hover:bg-emerald-600",
    tabActive: "data-[state=active]:bg-emerald-700 data-[state=active]:text-white dark:data-[state=active]:bg-emerald-600",
    border: "border-emerald-200 dark:border-emerald-800",
    soft: "bg-emerald-50 dark:bg-emerald-950/20",
    iconChip: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-400",
    gradient: "from-emerald-900 to-emerald-700",
    headerBg: "from-emerald-50 to-amber-50/60 border-emerald-100/70 dark:from-emerald-950/40 dark:to-amber-950/30 dark:border-emerald-900/40",
    titleHover: "group-hover:text-emerald-800 dark:group-hover:text-emerald-200",
  },
  GROUP: {
    icon: Users,
    typeLabel: "Group class",
    rail: "from-violet-500 to-indigo-500",
    accent: "text-violet-700 dark:text-violet-300",
    pill: "bg-violet-50 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300",
    dot: "bg-violet-500",
    solid: "bg-violet-600 hover:bg-violet-700 text-white dark:bg-violet-500 dark:hover:bg-violet-600",
    tabActive: "data-[state=active]:bg-violet-700 data-[state=active]:text-white dark:data-[state=active]:bg-violet-600",
    border: "border-violet-200 dark:border-violet-800",
    soft: "bg-violet-50 dark:bg-violet-950/20",
    iconChip: "bg-violet-100 text-violet-600 dark:bg-violet-900/50 dark:text-violet-400",
    gradient: "from-violet-900 to-violet-700",
    headerBg: "from-violet-50 to-indigo-50/60 border-violet-100/70 dark:from-violet-950/40 dark:to-indigo-950/30 dark:border-violet-900/40",
    titleHover: "group-hover:text-violet-800 dark:group-hover:text-violet-200",
  },
  CRASH_COURSE: {
    icon: Zap,
    typeLabel: "Crash course",
    rail: "from-amber-500 to-orange-500",
    accent: "text-amber-700 dark:text-amber-300",
    pill: "bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
    dot: "bg-amber-500",
    solid: "bg-amber-600 hover:bg-amber-700 text-white dark:bg-amber-500 dark:hover:bg-amber-600",
    tabActive: "data-[state=active]:bg-amber-700 data-[state=active]:text-white dark:data-[state=active]:bg-amber-600",
    border: "border-amber-200 dark:border-amber-800",
    soft: "bg-amber-50 dark:bg-amber-950/20",
    iconChip: "bg-amber-100 text-amber-600 dark:bg-amber-900/50 dark:text-amber-400",
    gradient: "from-amber-900 to-amber-700",
    headerBg: "from-amber-50 to-orange-50/60 border-amber-100/70 dark:from-amber-950/40 dark:to-orange-950/30 dark:border-amber-900/40",
    titleHover: "group-hover:text-amber-800 dark:group-hover:text-amber-200",
  },
};

/** Looks up the theme by offering type, falling back to 1-on-1 for unrecognized/empty values. */
export function getOfferingTypeTheme(type: OfferingType | string | null | undefined): OfferingTypeTheme {
  return OFFERING_TYPE_THEME[type as OfferingType] ?? OFFERING_TYPE_THEME.ONE_TO_ONE;
}
